import { Component, ChangeDetectionStrategy, OnInit, computed, inject, input, output } from '@angular/core'
import { Depot, Product, ProductDepot } from 'depoto-core/src/entities'
import { CoreService } from '../../../../services'

export type ProductDepotSelection = { productDepot: number; quantity: number }

type PickerRow = ProductDepot & { orderQuantityTmp?: number }
type DepotGroup = { depot: Depot; rows: PickerRow[] }

@Component({
  selector: 'product-depots-picker',
  templateUrl: './product-depots-picker.component.html',
  styleUrls: ['./product-depots-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductDepotsPickerComponent implements OnInit {
  private core = inject(CoreService)

  product = input<Product>()
  // Depot ids allowed by the order's checkout and the user's depots; null means unrestricted.
  depotScope = input<number[] | null>(null)
  existingProductDepots = input<ProductDepotSelection[]>([])
  // Mirrors modal-product-choose's own products.availability filter: 'all' means show every
  // row, same as the equivalent toggle in product-depots-form does when switched on.
  availability = input<string>('available')

  addProductDepot = output<ProductDepotSelection>()

  showEmptyPositions = computed(() => this.availability() === 'all')

  groups: DepotGroup[] = []
  isLoading = false
  private loaded = false

  async ngOnInit(): Promise<void> {
    await this.load()
  }

  private async load(): Promise<void> {
    if (this.loaded) {
      return
    }
    const product = this.product()
    if (!product?.id) {
      return
    }
    const allowed = this.depotScope()
    if (allowed?.length === 0) {
      // No depot is both on the checkout and visible to the user, so there is nothing to pick.
      this.groups = []
      this.loaded = true
      return
    }
    this.isLoading = true
    try {
      let productDepots: ProductDepot[] = product.productDepots ?? []
      if (!productDepots.length) {
        const r = await this.core.services.product.getById(product.id, {
          id: null,
          productDepots: {
            id: null,
            depot: { id: null, name: null },
            supplier: { id: null, name: null },
            purchasePrice: null,
            quantityStock: null,
            quantityReservation: null,
            quantityAvailable: null,
            batch: null,
            expirationDate: null,
            position: null,
          },
        })
        productDepots = (r?.productDepots ?? []).map(pd => new ProductDepot(pd))
        product.productDepots = productDepots
      }

      const filtered = allowed ? productDepots.filter(pd => allowed.includes(Number(pd.depot?.id))) : productDepots

      const byDepot = new Map<number, DepotGroup>()
      for (const pd of filtered) {
        // Default to 1 so a row is ready to add with a single click; preserve whatever the user
        // already typed if the panel is closed and reopened before adding.
        if ((pd as PickerRow).orderQuantityTmp === undefined) {
          ;(pd as PickerRow).orderQuantityTmp = 1
        }
        const depotId = Number(pd.depot?.id)
        if (!byDepot.has(depotId)) {
          byDepot.set(depotId, { depot: pd.depot as Depot, rows: [] })
        }
        byDepot.get(depotId).rows.push(pd)
      }
      this.groups = [...byDepot.values()].sort((a, b) => (a.depot?.name ?? '').localeCompare(b.depot?.name ?? ''))
    } finally {
      this.isLoading = false
      this.loaded = true
    }
  }

  alreadyAddedQuantity(row: PickerRow): number {
    const existing = this.existingProductDepots().find(pd => Number(pd.productDepot) === Number(row.id))
    return existing ? Number(existing.quantity) : 0
  }

  setQty(row: PickerRow, value: unknown): void {
    row.orderQuantityTmp = Number(value) || 0
  }

  // Adds just this one batch, with its own quantity - not the whole picker's selection.
  addRow(row: PickerRow): void {
    const quantity = Number(row.orderQuantityTmp)
    if (quantity <= 0) {
      return
    }
    this.addProductDepot.emit({ productDepot: Number(row.id), quantity })
  }
}
